import React, { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';


const SORT_OPTIONS = [
  { value: 'published_at',       label: 'Newest first' },
  { value: 'host_pct_desc',      label: 'Most host talk' },
  { value: 'host_pct_asc',       label: 'Most guest talk' },
  { value: 'turn_switches',      label: 'Most back-and-forth' },
  { value: 'longest_monologue_ms', label: 'Longest monologue' },
  { value: 'host_questions',     label: 'Most host questions' },
];

/**
 * Filter/sort controls for the episode list.
 * All state lives in URL query params so filters are shareable and back-navigable.
 *
 * @param {Object}   props
 * @param {string[]} [props.keywords]         - Keyword pills from latest API results
 * @param {Function} [props.onParamsChange]   - Called with current param object after debounce
 */
export default function EpisodeFilterBar({ keywords = [], onParamsChange, accentColor }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const sortBy     = searchParams.get('sort_by') || 'published_at';
  const order      = searchParams.get('order')   || 'desc';
  const search     = searchParams.get('search')  || '';
  const activeKeys = searchParams.getAll('keyword');

  // Debounce search input — avoid hammering the API on every keystroke
  const debounceRef = useRef(null);

  const setParam = useCallback((key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      // Reset cursor when filters change
      next.delete('cursor');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setParam('search', val), 300);
  }, [setParam]);

  const handleSortChange = useCallback((e) => {
    let newSortBy, newOrder;
    if (e.target.value === 'host_pct_desc') {
      newSortBy = 'host_pct'; newOrder = 'desc';
    } else if (e.target.value === 'host_pct_asc') {
      newSortBy = 'host_pct'; newOrder = 'asc';
    } else if (e.target.value === 'published_at') {
      newSortBy = 'published_at'; newOrder = 'desc';
    } else {
      // All other options are direct field names, always descending
      newSortBy = e.target.value; newOrder = 'desc';
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('sort_by', newSortBy);
      next.set('order', newOrder);
      next.delete('cursor');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const toggleKeyword = useCallback((kw) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const existing = next.getAll('keyword');
      next.delete('keyword');
      if (existing.includes(kw)) {
        existing.filter(k => k !== kw).forEach(k => next.append('keyword', k));
      } else {
        [...existing, kw].forEach(k => next.append('keyword', k));
      }
      next.delete('cursor');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Derived sort select value — map stored sort_by/order back to option value
  const sortSelectValue = sortBy === 'host_pct' && order === 'asc'
    ? 'host_pct_asc'
    : sortBy === 'host_pct'
      ? 'host_pct_desc'
      : ['turn_switches', 'longest_monologue_ms', 'host_questions'].includes(sortBy)
        ? sortBy
        : 'published_at';

  // Notify parent of param changes
  useEffect(() => {
    onParamsChange?.({ sort_by: sortBy, order, search, keywords: activeKeys });
  }, [sortBy, order, search, activeKeys.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up debounce on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const combinedKeywords = Array.from(new Set([...keywords, ...activeKeys]));

  return (
    <div>
      <div className="filter-bar" role="search" aria-label="Episode filters">
        <div className="filter-bar__search">
          <label htmlFor="episode-search" className="sr-only">Search episodes</label>
          <input
            id="episode-search"
            type="search"
            className="filter-bar__input"
            placeholder="Search episodes..."
            defaultValue={search}
            onChange={handleSearchChange}
            aria-label="Search episodes"
          />
        </div>

        <label htmlFor="sort-select" className="sr-only">Sort by</label>
        <select
          id="sort-select"
          className="filter-bar__select"
          value={sortSelectValue}
          onChange={handleSortChange}
          aria-label="Sort episodes"
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

      </div>

      {combinedKeywords.length > 0 && (
        <div style={accentColor ? { '--pill-color': accentColor } : undefined}>
          <p className="keyword-pills__label">Top words across this podcast</p>
          <div className="keyword-pills" role="group" aria-label="Top words — click to filter episodes">
            {combinedKeywords.map(kw => {
              const isActive = activeKeys.includes(kw);
              return (
                <button
                  key={kw}
                  type="button"
                  className={`pill pill--colored${isActive ? ' pill--active' : ''}`}
                  onClick={() => toggleKeyword(kw)}
                  aria-pressed={isActive}
                  aria-label={`${isActive ? 'Remove' : 'Add'} keyword filter: ${kw}`}
                >
                  {kw}
                  {isActive && (
                    <span className="pill__remove" aria-hidden="true">×</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
